/**
 * Copying text, on a page that is not served over HTTPS.
 *
 * navigator.clipboard only exists in a secure context. Pugo is reached over
 * plain http on an IP address, so on the web build it is simply undefined -
 * and every "Copy username" was quietly doing nothing while still saying it
 * had worked.
 *
 * The old execCommand path still works everywhere, so it is the fallback. The
 * result is returned rather than assumed, so a caller can avoid claiming
 * success it did not get.
 */
export async function copyText(text: string): Promise<boolean> {
  // The modern one, where it exists.
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Denied or unavailable - fall through and try the old way.
  }

  // The old one: a hidden, off-screen textarea that we select and copy from.
  try {
    const box = document.createElement('textarea');
    box.value = text;
    box.setAttribute('readonly', '');
    box.style.position = 'fixed';
    box.style.top = '-1000px';
    box.style.opacity = '0';
    document.body.appendChild(box);

    const selection = document.getSelection();
    const previous = selection && selection.rangeCount ? selection.getRangeAt(0) : null;

    box.select();
    box.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');

    document.body.removeChild(box);
    // Put back whatever the person had selected before we borrowed it.
    if (previous && selection) { selection.removeAllRanges(); selection.addRange(previous); }
    return ok;
  } catch {
    return false;
  }
}
